import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {ComponentCanDeactivate}                           from '../../../guards/ap-deactivate.guard';
import {
  ICropRotationMassEdit
}                                                         from '../../../ap-crop-rotation/interfaces/ap-crop-rotation-mass-edit.interface';
import {BehaviorSubject, combineLatest, of, Subscription} from 'rxjs';
import {CropTypeStore}                                    from '../../../stores/base-data/crop.types.store';
import {SettingsStore}                                    from '../../../stores/base-data/settings.store';
import {CampaignYearStore}                                from '../../../stores/login/campaignyear.store';
import {MapViewStore}                                     from '../../../stores/layout/mapview.store';
import {MenuStore}                                        from '../../../stores/layout/menu.store';
import {CropRotationStore}                                from '../../../stores/farm/crop.rotation.store';
import {ListSortOrder, ObjectFactory}                     from 'ts-tooling';
import {ApGetCropStatisticService}                        from '../../../stores/services/ap-get-crop-statistic.service';
import {StatisticStore}                                   from '../../../stores/statistic/statistic.store';
import {ApStatsType}                                      from '../../../ap-interface';
import {StatisticService}                                 from '../../../services/data/statistic.service';
import {map}                                              from 'rxjs/operators';
import ICropRotationCell = Data.FieldManagement.ICropRotationCell;
import ICropTypes = Data.BaseData.ICropTypes;
import ISettings = Data.BaseData.ISettings;
import ICropRotation = Data.FieldManagement.ICropRotation;
import IFieldCropRotation = Data.FieldManagement.IFieldCropRotation;

export const INPUT_IDLE_TIMEOUT = 750;

@Component({
  selector: 'ap-crop-rotation',
  templateUrl: './ap-crop-rotation.component.html',
  styleUrls: ['./ap-crop-rotation.component.scss']
})
export class ApCropRotationComponent extends ComponentCanDeactivate implements OnInit, AfterContentInit, OnDestroy {
  caption = 'Nutrients__CropRotation';
  filter = 'Global_Filter';
  currentYear: number;
  cropTypes: ICropTypes[] = [];
  allCropTypes: ICropTypes[] = [];
  settings: ISettings = null;
  items$ = this.cropRotationStore.FieldCropRotations$;
  loading$ = of(true);
  saving$ = this.cropRotationStore.FieldCropSaving$;
  mapViewMode$ = this.mapViewStore.Listen(s => s.mode);
  menuMode$ = this.menuStore.Listen(s => s.mode);
  searchText$ = new BehaviorSubject<string>('');
  clearAllFilters$ = new BehaviorSubject<boolean>(false);

  private _subscriptions: Subscription[] = [];
  private _revisions: [string, number][] = [];
  private _focusCells: string[] = [];
  private _selectedItemsOnGrid$ = new BehaviorSubject<IFieldCropRotation[]>([]);

  constructor(private cropRotationStore: CropRotationStore,
              private cropTypeStore: CropTypeStore,
              private campaignYearStore: CampaignYearStore,
              private mapViewStore: MapViewStore,
              private menuStore: MenuStore,
              private settingsStore: SettingsStore,
              private statisticStore: StatisticStore,
              private cropStatisticService: ApGetCropStatisticService,
              private statisticService: StatisticService) {
    super();
  }

  @Output()
  public clearAllFilters = new EventEmitter<void>();

  ngOnInit(): void {
    this.mapViewStore.hideMapView();
    this.loading$ = combineLatest([
      this.cropRotationStore.FieldCropRotationsLoading$,
      this.settingsStore.Loading$
    ]).pipe(
      map(loadingItems => loadingItems.some(x => x))
    );
  }

  ngAfterContentInit(): void {
    this._subscriptions.push(this.cropTypeStore.CropTypes$.subscribe(d => {
      this.allCropTypes = d;
      this.cropTypes = d.FindAll(_ => _.Selected).SortBy(['Description'], [ListSortOrder.ASC]);
    }));
    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe((d) => {
      this.currentYear = !d ? 2019 : d.Year;
    }));
    this._subscriptions.push(this.settingsStore.FirstSetting$.subscribe(s => this.settings = s));
    this._subscriptions.push(combineLatest([
      this.cropRotationStore.FieldCropRotations$,
      this._selectedItemsOnGrid$.asObservable()
    ]).subscribe(([fieldCropRotation, selectedItemsOnGrid]) => {
      if (selectedItemsOnGrid.length <= 0) {
        this._generateStatistic(fieldCropRotation, fieldCropRotation);
      } else {
        this._generateStatistic(fieldCropRotation, selectedItemsOnGrid);
      }
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions?.forEach(x => x?.unsubscribe());
    this.statisticStore.removeStatisticData();
  }

  canDeactivate(): boolean {
    this.forceSaveMarkedCells();
    return true;
  }

  onSelectedItem(selectedFieldCropRotations: IFieldCropRotation[]): void {
    this._selectedItemsOnGrid$.next(selectedFieldCropRotations);
  }

  onSaveCell(e: ICropRotationCell): void {
    const revision = this.getCellRevision(e);
    revision[1]++;
    this.cropRotationStore.MarkForSaving([e]);
    setTimeout(this.checkForSave.bind(this, ObjectFactory.Copy(revision[1]), e), INPUT_IDLE_TIMEOUT);
  }

  onCellGetFocus(e: [string, number]): void {
    const id = e[0] + '#' + e[1];
    this._focusCells = this._focusCells.filter(c => c !== id);
    this._focusCells.push(id);
  }

  onCellLostFocus(e: [string, number]): void {
    this._focusCells = this._focusCells.filter(id => id !== e[0] + '#' + e[1]);
  }

  onMassEditChange(e: ICropRotationMassEdit<ICropRotationCell>): void {
    this.cropRotationStore.ProcessCells(e.Ids.map(i => {
      const original = this.cropRotationStore.CropRotation.find(c => c.Id === i).Yields[e.Value.YearIndex + 1];
      const mainYield = e.Value.MainCropYield !== undefined ? parseFloat(e.Value.MainCropYield.toString()) : original.MainCropYield;
      const secondYield = e.Value.SecondCropYield !== undefined ? parseFloat(e.Value.SecondCropYield.toString()) : original.SecondCropYield;
      return {
        MainCrop: e.Value.MainCrop !== undefined ? e.Value.MainCrop : original.MainCrop,
        SecondCrop: e.Value.SecondCrop !== undefined ? e.Value.SecondCrop : original.SecondCrop,
        Year: e.Value.Year,
        MainCropStraw: e.Value.MainCropStraw,
        YearIndex: e.Value.YearIndex,
        FieldId: i,
        MainCropYield: isNaN(mainYield) ? 0 : mainYield,
        SecondCropYield: isNaN(secondYield) ? 0 : secondYield,
      };
    }));
  }

  onMassEditRemoveMainCrop(e: any): void {
    this.cropRotationStore.ProcessCells(e.Ids.map(i => {
      const dataset: ICropRotation = this.cropRotationStore.CropRotation.find(i2 => i2.Id === i);
      if (!dataset) {
        return null;
      }
      return {
        ...e.Value,
        FieldId: i,
        MainCrop: null,
        SecondCrop: dataset.Yields[e.Value.YearIndex + 1]?.SecondCrop,
        SecondCropYield: dataset.Yields[e.Value.YearIndex + 1]?.SecondCropYield
      };
    }));
  }

  onMassEditRemoveSecondCrop(e: any): void {
    this.cropRotationStore.ProcessCells(e.Ids.map(i => {
      const dataset: ICropRotation = this.cropRotationStore.CropRotation.find(i2 => i2.Id === i);
      if (!dataset) {
        return null;
      }
      return {
        ...e.Value,
        FieldId: i,
        MainCrop: dataset.Yields[e.Value.YearIndex + 1]?.MainCrop,
        MainCropYield: dataset.Yields[e.Value.YearIndex + 1]?.MainCropYield,
        MainCropStraw: dataset.Yields[e.Value.YearIndex + 1]?.MainCropStraw,
        SecondCrop: null,
        SecondCropYield: null,
      };
    }).filter(i => !!i));
  }

  private _generateStatistic(totalItems: IFieldCropRotation[], selectedItems: IFieldCropRotation[]): void {
    const dataForTitles = totalItems.map(x => ({
      AreaHa: x.AreaHa,
      Selected: selectedItems.some(z => z.Id === x.Id)
    }));
    const title = this.statisticService.generateFieldsAreaTitle(dataForTitles);
    const fieldCropRotations = selectedItems && selectedItems.length > 0
      ? selectedItems
      : totalItems;
    const selectedYear = this.campaignYearStore.SelectedCampaignYear?.Year;
    if (selectedYear) {
      const startYear = selectedYear - 1;
      const endYear = selectedYear + 5;
      const data = this.cropStatisticService.gatherFieldCropRotationData(fieldCropRotations, startYear, endYear);
      this.statisticStore.setStatisticData(data, ApStatsType.FieldsCropRotationStats, title, '');
    }
  }

  private checkForSave(rev: number, cell: ICropRotationCell): void {
    const revision = this.getCellRevision(cell);
    if (this._focusCells.find(i => i === cell.FieldId.toString() + '#' + cell.Year)) {
      setTimeout(this.checkForSave.bind(this, rev, cell), INPUT_IDLE_TIMEOUT);
      return;
    }
    if (rev === revision[1]) {
      this.saveChanges(revision[0]);
      this.resetCellRevision(cell);
    }
  }

  private saveChanges(revisionId: string): void {
    const cellIdInfo = this.fromRevisionId(revisionId);
    const moveMarked = this.cropRotationStore.MarkedForSaving.filter(i => i.FieldId.toString() === cellIdInfo[0] && i.Year === cellIdInfo[1]);
    this.cropRotationStore.ProcessCells(moveMarked);
    this.cropRotationStore.UnmarkFromSaving(moveMarked);
  }

  private forceSaveMarkedCells(): void {
    const dataToSave = this.cropRotationStore.MarkedForSaving.length > 0;
    if (dataToSave) {
      this.cropRotationStore.ProcessCells(this.cropRotationStore.MarkedForSaving);
      this.cropRotationStore.UnmarkFromSaving(this.cropRotationStore.MarkedForSaving);
    }
  }

  private getCellRevision(cell: ICropRotationCell): [string, number] {
    const id = this.toRevisionId(cell);
    let revision = this._revisions.find(i => i[0] === id);
    if (!revision) {
      revision = [id, 0];
      this._revisions.push(revision);
    }
    return revision;
  }

  private resetCellRevision(cell: ICropRotationCell): void {
    const id = this.toRevisionId(cell);
    this._revisions = this._revisions.filter(i => i[0] !== id);
  }

  private toRevisionId(cell: ICropRotationCell): string {
    return cell.FieldId.toString() + '#' + cell.Year;
  }

  private fromRevisionId(id: string): [string, number] {
    const tmp = id.split('#');
    return [tmp[0], parseInt(tmp[1], 10)];
  }

  public performSearch(searchText: string): void {
    this.searchText$.next(searchText);
  }

  public clearFilters(): void {
    this.clearAllFilters$.next(true);
  }
}
